import { useEffect } from "react";

import MOBILE from "./mobileConfig";
import { isIOSWebKit } from "./useIsMobile";

/**
 * The page's scroll lock on mobile — ONE lock, shared and counted.
 *
 * WHY <html>. phrResponsive's `html, body { overflow-x: hidden }` makes <html>
 * the page's scroller at every width, so body's overflow never reaches the
 * viewport. A lock on <body> alone — which is what most desktop dialogs take,
 * and what ModalPortal holds while any portal is mounted — leaves the page free
 * to scroll behind the overlay. Measured on mobile Create Rx.
 *
 * WHY COUNTED — the same pattern ModalPortal uses for <body>. Each holder used
 * to save the overflow it found and put that back on release, which breaks the
 * moment two hold it: the second saves the first's "hidden". When both go in
 * one commit — a nested sheet's row navigating away — React 16 runs unmount
 * cleanups parent first, so the inner holder's restore of "hidden" lands last
 * and the next screen cannot scroll (Step 74). Counted, the order does not
 * matter: the last holder out puts back what was there before the first came in.
 */
let holders = 0;
let saved = null;

/**
 * iOS: A FINGER PANS WHAT `overflow: hidden` CANNOT HOLD (Step 86,
 * `MOBILE.iosTouchScrollLock`).
 *
 * With the keyboard up, the visible area on an iPhone is shorter than the page,
 * and WebKit lets a drag pan it over the page — `overflow` has no say in that.
 * So a drag on the Account code popover over the filter sheet moved the
 * appointment list behind it. Only cancelling the `touchmove` stops it.
 *
 * While the lock is held, a one-finger drag is cancelled UNLESS something under
 * the finger can still scroll the way it is moving — the popover's list, a
 * sheet's body, a dialog's body. At that scroller's end it is cancelled too, so
 * the drag cannot carry on into the page. Two fingers are never touched:
 * pinch-zoom stays the user's.
 *
 * iOS alone (isIOSWebKit), as reported; Android and desktop keep the overflow
 * lock alone.
 */
const SCROLLER = /(auto|scroll|overlay)/;
let guarded = false;
let lastX = 0;
let lastY = 0;

/* Can `el`, or a scroller between it and the page, move the way the finger
   does? A finger moving DOWN (dy > 0) asks to see what is ABOVE. */
const canPan = (el, dx, dy) => {
  const vertical = Math.abs(dy) >= Math.abs(dx);
  for (
    let n = el;
    n && n.nodeType === 1 && n !== document.body && n !== document.documentElement;
    n = n.parentElement
  ) {
    const cs = window.getComputedStyle(n);
    if (vertical) {
      if (SCROLLER.test(cs.overflowY) && n.scrollHeight > n.clientHeight) {
        if (dy > 0 ? n.scrollTop > 0 : n.scrollTop + n.clientHeight < n.scrollHeight - 1) {
          return true;
        }
      }
    } else if (SCROLLER.test(cs.overflowX) && n.scrollWidth > n.clientWidth) {
      if (dx > 0 ? n.scrollLeft > 0 : n.scrollLeft + n.clientWidth < n.scrollWidth - 1) {
        return true;
      }
    }
  }
  return false;
};

const onTouchStart = (e) => {
  if (e.touches.length !== 1) return;
  lastX = e.touches[0].clientX;
  lastY = e.touches[0].clientY;
};

const onTouchMove = (e) => {
  if (e.touches.length !== 1 || !e.cancelable) return;
  const { clientX, clientY } = e.touches[0];
  const dx = clientX - lastX;
  const dy = clientY - lastY;
  lastX = clientX;
  lastY = clientY;
  if (!dx && !dy) return;
  if (!canPan(e.target, dx, dy)) e.preventDefault();
};

/* `passive: false` is the point — iOS makes document touch listeners passive
   by default, and a passive one cannot cancel. Capture, so a handler that
   stops propagation further down cannot hide a drag from it. */
const guardTouch = () => {
  if (!MOBILE.iosTouchScrollLock || !isIOSWebKit()) return;
  document.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  document.addEventListener("touchmove", onTouchMove, { capture: true, passive: false });
  guarded = true;
};

const unguardTouch = () => {
  if (!guarded) return;
  document.removeEventListener("touchstart", onTouchStart, { capture: true });
  document.removeEventListener("touchmove", onTouchMove, { capture: true });
  guarded = false;
};

export const lockScroll = () => {
  if (holders === 0) {
    saved = {
      body: document.body.style.overflow,
      root: document.documentElement.style.overflow,
    };
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    guardTouch();
  }
  holders += 1;
};

export const unlockScroll = () => {
  holders -= 1;
  if (holders > 0) return;
  document.body.style.overflow = saved.body;
  document.documentElement.style.overflow = saved.root;
  saved = null;
  unguardTouch();
};

/**
 * Holds the lock while `active` is true.
 *
 * For overlays this layer does not own — a desktop dialog a mobile screen
 * renders, whose own lock is on <body> alone or missing — so the screen that
 * knows the dialog is open holds the lock for it. The dialog itself is left
 * exactly as desktop has it.
 */
export const useScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    lockScroll();
    return unlockScroll;
  }, [active]);
};
